import { ReactNode } from 'react';
import { m, AnimatePresence } from 'framer-motion';

// One reusable modal so every overlay in the app shares the same backdrop and
// surface treatment instead of each screen hand-rolling its own. Two layouts:
//   - 'sheet'  : bottom sheet that slides up (game options, long content)
//   - 'center' : small centered dialog (confirmations)
export function Modal({ open, onClose, title, children, maxWidth = 520, variant = 'sheet' }: {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  maxWidth?: number;
  variant?: 'sheet' | 'center';
}) {
  const centered = variant === 'center';
  return (
    <AnimatePresence>
      {open && (
        <m.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
            zIndex: 1000, display: 'flex', justifyContent: 'center',
            alignItems: centered ? 'center' : 'flex-end',
            padding: centered ? '1.5rem' : 0,
          }}
        >
          <m.div
            initial={centered ? { scale: 0.9, opacity: 0, y: 10 } : { y: 40, opacity: 0 }}
            animate={centered ? { scale: 1, opacity: 1, y: 0 } : { y: 0, opacity: 1 }}
            exit={centered ? { scale: 0.9, opacity: 0, y: 10 } : { y: 40, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 320, damping: centered ? 26 : 30 }}
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            style={{
              width: '100%', maxWidth, background: '#1B1830', border: '1px solid #2E2B45',
              borderRadius: centered ? '20px' : '24px 24px 0 0',
              padding: '1.5rem',
              maxHeight: '88vh', overflowY: 'auto',
              boxShadow: centered ? 'var(--hard-shadow-lg)' : 'none',
              paddingBottom: centered ? '1.5rem' : 'max(1.5rem, env(safe-area-inset-bottom))',
            }}
          >
            {title && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
                <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#fff' }}>{title}</h2>
                <button
                  onClick={onClose}
                  aria-label="Close"
                  style={{ background: 'none', border: 'none', color: '#888', fontSize: '1.5rem', cursor: 'pointer', lineHeight: 1 }}
                >
                  ✕
                </button>
              </div>
            )}
            {children}
          </m.div>
        </m.div>
      )}
    </AnimatePresence>
  );
}
